if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initMainGalleryNav();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initVerticalThumbnails();
      }

      get isVerticalThumbnails() {
        return this.dataset.desktopLayout === 'thumbnail_vertical' && this.mql.matches;
      }

      getMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      initMainGalleryNav() {
        if (!this.elements.viewer) return;

        this.prevButton = this.elements.viewer.querySelector('.kg-media-gallery-nav button[name="previous"]');
        this.nextButton = this.elements.viewer.querySelector('.kg-media-gallery-nav button[name="next"]');
        if (!this.prevButton || !this.nextButton) return;

        this.onMainGalleryNavClick = this.onMainGalleryNavClick.bind(this);
        this.prevButton.addEventListener('click', this.onMainGalleryNavClick, true);
        this.nextButton.addEventListener('click', this.onMainGalleryNavClick, true);

        this.elements.viewer.addEventListener('slideChanged', () => this.updateMainGalleryNavState());
        this.mql.addEventListener('change', () => this.updateMainGalleryNavState());
        this.updateMainGalleryNavState();
      }

      onMainGalleryNavClick(event) {
        const items = this.getMediaItems();
        if (items.length < 2) return;

        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') || items[0];
        const currentIndex = items.indexOf(activeMedia);
        if (currentIndex < 0) return;

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= items.length) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      updateMainGalleryNavState() {
        if (!this.prevButton || !this.nextButton) return;

        const items = this.getMediaItems();
        if (items.length < 2) {
          this.prevButton.setAttribute('disabled', 'disabled');
          this.nextButton.setAttribute('disabled', 'disabled');
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') || items[0];
        const currentIndex = Math.max(0, items.indexOf(activeMedia));

        if (currentIndex <= 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }

        if (currentIndex >= items.length - 1) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }

      initVerticalThumbnails() {
        if (this.dataset.desktopLayout !== 'thumbnail_vertical') return;

        this.syncVerticalThumbnailHeight = this.syncVerticalThumbnailHeight.bind(this);

        this.verticalResizeObserver = new ResizeObserver(() => this.syncVerticalThumbnailHeight());
        if (this.elements.viewer) this.verticalResizeObserver.observe(this.elements.viewer);

        this.mql.addEventListener('change', this.syncVerticalThumbnailHeight);

        // Wait for layout/images so gallery height is accurate
        requestAnimationFrame(() => {
          this.syncVerticalThumbnailHeight();
          window.setTimeout(() => this.syncVerticalThumbnailHeight(), 150);
          window.setTimeout(() => this.syncVerticalThumbnailHeight(), 500);
        });
      }

      syncVerticalThumbnailHeight() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        if (!this.isVerticalThumbnails) {
          this.elements.thumbnails.style.height = '';
          this.elements.thumbnails.style.maxHeight = '';
          return;
        }

        const viewerHeight = Math.ceil(this.elements.viewer.getBoundingClientRect().height);
        if (!viewerHeight) return;

        this.elements.thumbnails.style.height = `${viewerHeight}px`;
        this.elements.thumbnails.style.maxHeight = `${viewerHeight}px`;

        if (typeof this.elements.thumbnails.resetPages === 'function') {
          this.elements.thumbnails.resetPages();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateMainGalleryNavState();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateMainGalleryNavState();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        this.syncVerticalThumbnailHeight();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button.thumbnail')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button.thumbnail')?.setAttribute('aria-current', true);

        if (this.isVerticalThumbnails) {
          const slider = this.elements.thumbnails.slider;
          if (!slider) return;
          const offset = 10;
          const visibleTop = slider.scrollTop + offset;
          const visibleBottom = slider.scrollTop + slider.clientHeight - offset;
          const thumbTop = thumbnail.offsetTop;
          const thumbBottom = thumbTop + thumbnail.clientHeight;
          if (thumbTop >= visibleTop && thumbBottom <= visibleBottom) {
            return;
          }
          slider.scrollTo({ top: Math.max(thumbTop - offset, 0) });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
